// composables/v3/useSessionLifecycle.ts — revalidation + cross-tab sync (SPEC §7)
// Listeners are attached exactly once per page load (module-level flag) and
// detached when the owning scope (layouts/v3.vue) goes away.
import { getCurrentInstance, onUnmounted, watch } from 'vue'
import { navigateTo, useRouter } from '#imports'
import type { Unsubscribe } from '~/types/v3/datasource'
import type { SessionEndReason } from '~/types/v3/session'
import { onCartChanged, onSessionCleared } from '~/services/v3/cache/tabSync'
import { useBootstrapStore } from '~/store/v3/bootstrap'
import { useSessionStore } from '~/store/v3/session'
import { useCartStore } from '~/store/v3/cart'

const REVALIDATE_INTERVAL_MS = 30_000

/** Same public set as `middleware/v3-session.global.ts` — never redirect off these. */
const PUBLIC_V3_PATHS = new Set(['/v3/qrcode', '/v3/login', '/v3/invalid'])

let attached = false
let detachers: Unsubscribe[] = []

function detach(): void {
  for (const off of detachers) {
    try { off() } catch (e) { console.warn('[v3/lifecycle] detach failed', e) }
  }
  detachers = []
  attached = false
}

function routeForReason(reason: SessionEndReason): string {
  if (reason === 'invalid') return '/v3/invalid'
  // 'switch': another tab scanned a new QR code — go back to the boot page so
  // the new session is picked up from localStorage.
  if (reason === 'switch') return '/v3'
  return '/v3/login'
}

export interface SessionLifecycle {
  /** Detaches every listener (also called automatically on scope unmount). */
  stop(): void
}

export function useSessionLifecycle(): SessionLifecycle {
  if (typeof window === 'undefined') return { stop: () => {} }

  if (!attached) {
    attached = true

    const bootstrap = useBootstrapStore()
    const session = useSessionStore()
    const cart = useCartStore()

    const revalidate = (): void => {
      void bootstrap.revalidate().catch((e) => console.warn('[v3/lifecycle] revalidate failed', e))
    }

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') revalidate()
    }
    document.addEventListener('visibilitychange', onVisibility)
    detachers.push(() => document.removeEventListener('visibilitychange', onVisibility))

    const onFocus = (): void => revalidate()
    window.addEventListener('focus', onFocus)
    detachers.push(() => window.removeEventListener('focus', onFocus))

    const timer = window.setInterval(revalidate, REVALIDATE_INTERVAL_MS)
    detachers.push(() => window.clearInterval(timer))

    detachers.push(onSessionCleared((reason) => {
      if (session.status !== 'ended') bootstrap.teardown(reason)
      void navigateTo(routeForReason(reason), { replace: true })
    }))

    detachers.push(onCartChanged((key) => {
      if (key && key === cart.key) void cart.restore(key)
    }))

    // A session can end while the guest is deep in the flow (the table is paid
    // or the token expires while the menu is open). `pages/v3/index.vue` owns
    // that redirect for '/v3'; every other protected page needs it here, or the
    // guest keeps browsing a menu they can no longer order from.
    const router = useRouter()
    const stopStatusWatch = watch(
      () => bootstrap.status,
      (status) => {
        if (status !== 'expired') return
        const path = router.currentRoute.value.path.replace(/\/+$/, '') || '/'
        if (path === '/v3' || PUBLIC_V3_PATHS.has(path)) return
        void navigateTo('/v3/login', { replace: true })
      },
    )
    detachers.push(stopStatusWatch)
  }

  if (getCurrentInstance()) onUnmounted(detach)

  return { stop: detach }
}
